import React from 'react';
import './Study.css';

/**
 * One layout for every page outside the inbox (advisor §3.7): a 760 px column, each
 * question in a white card with the question as an h3 (never a <legend>), 44 px radio
 * rows, a progress caption, and a primary button that says why it is disabled.
 */
export interface StudyPageProps {
  title: string;
  lead?: React.ReactNode;
  progress?: string;
  formTestId?: string;
  submitTestId?: string;
  primaryLabel?: string;
  canSubmit?: boolean;
  remaining?: number;
  onSubmit?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const StudyPage: React.FC<StudyPageProps> = ({ title, lead, progress, formTestId, submitTestId, primaryLabel, canSubmit = true, remaining = 0, onSubmit, children, footer }) => {
  const body = (
    <>
      {children}
      {primaryLabel && (
        <div className="study-actions">
          <button type="submit" className="btn btn-primary study-primary" data-testid={submitTestId} disabled={!canSubmit}>{primaryLabel}</button>
          {!canSubmit && remaining > 0 && <span className="study-hint">Answer every question to continue ({remaining} left)</span>}
        </div>
      )}
      {footer}
    </>
  );
  return (
    <div className="study-page">
      <div className="study-col">
        {progress && <div className="study-progress">{progress}</div>}
        <h2 className="study-title">{title}</h2>
        {lead && <p className="study-lead">{lead}</p>}
        {onSubmit ? (
          <form data-testid={formTestId} onSubmit={e => { e.preventDefault(); if (canSubmit) onSubmit(); }}>{body}</form>
        ) : body}
      </div>
    </div>
  );
};

export default StudyPage;

/** A question card with radio rows. */
export const RadioQuestion: React.FC<{
  name: string; text: React.ReactNode; options: Array<{ key: string; label: string }>; value: string | undefined;
  onChange: (v: string) => void; testId?: string; children?: React.ReactNode; number?: number;
}> = ({ name, text, options, value, onChange, testId, children, number }) => (
  <div className="study-card study-question" data-testid={testId} data-question={name}>
    <h3 className="study-q">{number ? `${number}. ` : ''}{text}</h3>
    <div className="study-options" role="radiogroup">
      {options.map(o => (
        <label key={o.key} className={`study-radio${value === o.key ? ' checked' : ''}`}>
          <input type="radio" name={name} value={o.key} checked={value === o.key} onChange={() => onChange(o.key)} />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
    {children}
  </div>
);

/** A labelled scale card: every point gets a number and, on 5-point scales, a label. */
export const ScaleQuestion: React.FC<{
  name: string; text: React.ReactNode; min: number; max: number; labels?: string[]; minLabel: string; maxLabel: string;
  value: number | undefined; onChange: (v: number) => void; testId?: string; number?: number;
}> = ({ name, text, min, max, labels, minLabel, maxLabel, value, onChange, testId, number }) => {
  const points = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  return (
    <div className="study-card study-question" data-testid={testId} data-question={name}>
      <h3 className="study-q">{number ? `${number}. ` : ''}{text}</h3>
      <div className={`study-scale points-${points.length}`} role="radiogroup">
        {points.map((n, i) => (
          <label key={n} className={`study-scale-point${value === n ? ' checked' : ''}`}>
            <input type="radio" name={name} value={n} checked={value === n} onChange={() => onChange(n)} />
            <span className="study-scale-num">{n}</span>
            {labels && labels[i] !== undefined && <span className="study-scale-lbl">{labels[i]}</span>}
          </label>
        ))}
      </div>
      {(!labels || !labels[0] || !labels[points.length - 1]) && (
        <div className="study-anchors"><span>{minLabel}</span><span>{maxLabel}</span></div>
      )}
    </div>
  );
};

/** Plain card (no question) for lead text or instructions. */
export const StudyCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`study-card ${className || ''}`}>{children}</div>
);

/** Renders **bold** and *italic* markers in card/bank text. */
export const Bold: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith('*')) return <em key={i}>{p.slice(1, -1)}</em>;
        return <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </>
  );
};
